import React from 'react';
import { cn } from '@/utils/cn';
import type { MemberEditData, RoleOption, TeamOption } from '../types/memberEdit.types';

interface MemberEditFormBlockProps {
  formData: Partial<MemberEditData>;
  setFormData: (data: Partial<MemberEditData>) => void;
  roles: RoleOption[];
  teams: TeamOption[];
  isLoading?: boolean;
}

export function MemberEditFormBlock({ formData, setFormData, roles, teams, isLoading }: MemberEditFormBlockProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="h-4 w-32 rounded bg-slate-50" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-10 rounded bg-slate-50" />
          <div className="h-10 rounded bg-slate-50" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-2">
      <h2 className="text-sm font-medium text-slate-900 flex items-center gap-2">
        <i className="ri-user-settings-line text-emerald-600" />
        Thông tin định danh
      </h2>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Họ và tên</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName || ''}
            onChange={handleChange}
            placeholder="Nhập họ và tên..."
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Địa chỉ Email</label>
          <input
            type="email"
            name="email"
            value={formData.email || ''}
            onChange={handleChange}
            placeholder="example@gmail.com"
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Số điện thoại</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone || ''}
            onChange={handleChange}
            placeholder="09xx xxx xxx"
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Trạng thái</label>
          <select
            name="status"
            value={formData.status || 'active'}
            onChange={handleChange}
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-emerald-500 transition-all"
          >
            <option value="active">Hoạt động</option>
            <option value="pending">Chờ xác nhận</option>
            <option value="locked">Đã khóa</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Vai trò hệ thống</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {roles.map(role => (
              <label key={role.code} className="flex items-center space-x-2 border border-slate-200 p-2 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                <input 
                  type="checkbox" 
                  name="roleCodes" 
                  value={role.code}
                  checked={formData.roleCodes?.includes(role.code)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const currentRoles = formData.roleCodes || [];
                    const newRoles = checked 
                      ? [...currentRoles, role.code]
                      : currentRoles.filter(r => r !== role.code);
                    setFormData({ ...formData, roleCodes: newRoles });
                  }}
                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-slate-700">{role.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500 ml-1">Đội/Nhóm làm việc</label>
          <select
            name="teamId"
            value={formData.teamId || ''}
            onChange={handleChange}
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-emerald-500 transition-all font-medium text-slate-700"
          >
            <option value="">Chọn đội/nhóm...</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
